import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Space } from "antd";
import MainLayout from "../components/Layout/MainLayout";
import SearchInput from "../components/Forms/SearchInput";
import { FaCaretDown } from "react-icons/fa";
import DataTable from "../components/Tables/Datatable";
import { items, orderStatus, PERMISSION_IDS } from "../constant";
import { postApi } from "../services/api";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { formatDate, handleRouteNav, roundDecimal } from "../utils";
import Loader from "../common/Loader";
import Filters from "../components/Filters";
import { useSelector } from "react-redux";
import usePagination from "../hooks/usePagination";

export const OrderMorePagesButtons = ({ activeButton, isInternalPage = true, selectedClient }) => {
  const navigate = useNavigate();
  const { role_id } = useSelector((state) => state.auth);

  const navButtons = [
    {
      id: "outstandings",
      label: "Outstanding",
      path: "/orders/outstanding",
      state: { selectedClient },
    },
    {
      id: "sale-balance",
      label: "Sale Balance",
      path: "/orders/sale-balance",
      state: { selectedClient },
    },
  ].filter((btn) => !btn.hideFromInternal || !isInternalPage);

  const nonActiveClass = "bg-transparent border font-bold hover:!bg-[#A33434] border-[#A33434] text-[#000000] text-base";
  const activeClass = "bg-transparent font-bold border !bg-[#A33434] hover:!bg-[#A33434] border-[#A33434] text-[#fff] text-base";

  return (
    <div className="flex flex-wrap items-center gap-5 mb-5">
      {navButtons.map((btn) => (
        <Button
          key={btn.id}
          type="primary"
          className={activeButton === btn.id ? activeClass : nonActiveClass}
          shape="round"
          size="large"
          onClick={btn.onClick ? btn.onClick : () => navigate(handleRouteNav(btn.path, role_id), { state: btn.state })}
        >
          {btn.label}
        </Button>
      ))}
    </div>
  );
};

const Orders = () => {
  const { appliedFilters, DrawerUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "status",
        label: "Order Status",
        options: Object.entries(orderStatus).map(([key, value]) => ({
          label: value,
          value: value,
        })),
      },
      {
        type: "daterange",
        key: "orderPlaced",
        label: "Order Placed",
      },
      {
        type: "daterange",
        key: "dispatchDate",
        label: "Dispatch Date",
      },
      {
        type: "select",
        key: "quantity",
        label: "Total Quantity",
        options: [
          { label: "< 1000m", value: "-1000" },
          { label: "1000 - 5000m", value: "1000-5000" },
          { label: "5000 - 10000m", value: "5000-10000" },
          { label: "> 10000m", value: "10000-" },
        ],
      },
    ],
    onChange: () => resetPage(),
  });

  const { is_sales_person, is_admin, is_sales_manager, user_id, is_customer, role_id, permission_ids } = useSelector((state) => state.auth);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const { pagination, setPagination, handleChange: handlePaginationChange, resetPage } = usePagination();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const fetchIdRef = useRef(0);

  const fetchOrders = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setLoading(true);
      const { status, quantity, orderPlaced, dispatchDate } = appliedFilters;
      const [minTotalQty, maxTotalQty] = quantity ? quantity.split("-").map(Number) : [];

      const filters = {
        status,
        minTotalQty: minTotalQty || undefined,
        maxTotalQty: maxTotalQty || undefined,
        start_date: orderPlaced?.[0] ? formatDate(new Date(orderPlaced[0]), { format: "YYYY-MM-DD" }) : undefined,
        end_date: orderPlaced?.[1] ? formatDate(new Date(orderPlaced[1]), { format: "YYYY-MM-DD" }) : undefined,
        start_dispatch_date: dispatchDate?.[0] ? formatDate(new Date(dispatchDate[0]), { format: "YYYY-MM-DD" }) : undefined,
        end_dispatch_date: dispatchDate?.[1] ? formatDate(new Date(dispatchDate[1]), { format: "YYYY-MM-DD" }) : undefined,
      };

      const response = await postApi(
        "sales/order/get-sale-order",
        {
          search: query,
          page: pagination.current,
          per_page: pagination.pageSize,
          ...(Object.keys(appliedFilters).length ? filters : {}),
          ...(is_sales_person && !permission_ids.includes(PERMISSION_IDS.UNIVERSAL_ORDER_MANAGEMENT) ? { user_id } : {}),
        },
        null,
      );

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const formattedRows = (response?.data || []).map((order) => {
        const uniquePids = new Set();
        order.items?.forEach((item) => uniquePids.add(item.sort_no));

        let first_item_name = order.items[0]?.short_name;
        const displayProduct = uniquePids.size > 1 ? `${first_item_name} + ${uniquePids.size - 1} more` : first_item_name || "-";

        const total_qty = order.items.reduce((t, i) => t + (+i.update_total_meter || +i.quantity_meters), 0);

        return {
          ...order,
          clientName: order.customer?.company_name || "-",
          products: displayProduct,
          totalCases: order.items.reduce((t, i) => t + (+i.update_total_cases || +i.cases), 0),
          totalQuantityMeters: roundDecimal(total_qty),
          salesPerson: order.assign_user?.full_name || "-",
        };
      });

      setOrderData(formattedRows);
      setTotalOrders(response?.total || 0);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("Error fetching orders:", error);
    } finally {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setLoading(false);
    }
  }, [appliedFilters, is_sales_person, pagination.current, pagination.pageSize, query, user_id]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrders();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchOrders]);

  const columns = [
    {
      title: "ID",
      dataIndex: "order_id",
      key: "order_id",
      ellipsis: true,
      width: 100,
    },
    {
      title: "Clients",
      dataIndex: "clientName",
      key: "clientName",
      sortOrder: sortedInfo.columnKey === "clientName" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Products",
      dataIndex: "products",
      key: "products",
      ellipsis: true,
    },
    {
      title: "Total Quantity",
      key: "totalCases",
      render: (item) => `${item.totalCases} cases (${item.totalQuantityMeters}m)`,
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, item) => {
        let dateLabel = "";

        if (item.status === orderStatus.dispatched || item.status === orderStatus.parts_dispatched) {
          dateLabel = formatDate(item.dispatch_date);
        } else if (item.status === orderStatus.cancelled || item.status === orderStatus.parts_cancelled) {
          dateLabel = formatDate(item.cancel_date);
        } else if (item.status === orderStatus.placed) {
          dateLabel = formatDate(item.created_at);
        }

        return `${item.status}${dateLabel ? ` (${dateLabel})` : ""}`;
      },
      ellipsis: true,
    },
    (is_sales_manager || is_admin || permission_ids.includes(PERMISSION_IDS.UNIVERSAL_ORDER_MANAGEMENT)) && {
      title: "Assigned Sales",
      dataIndex: "salesPerson",
      key: "salesPerson",
      ellipsis: true,
    },
  ].filter(Boolean);

  const handleChange = (paginationInfo, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    handlePaginationChange(paginationInfo, filters, sorter);
  };

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRow = (record) => {
    navigate(handleRouteNav(`/orders/${record.order_id}`, role_id));
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    if (!is_customer) {
      setTitle("Orders");
      setDescription(`${totalOrders} total orders`);
      setBackButton(false);
    }
  }, [setTitle, setDescription, setBackButton, totalOrders]);

  return (
    <>
      <DrawerUI />
      <div className="bg-[#FFFFFF]" style={{ borderRadius: "32px", overflow: "hidden" }}>
        <div className="flex justify-between gap-7 items-center px-8 py-6">
          <SearchInput onSearch={onSearch} value={query} onChange={handleOnSearchChange} placeholder="Search for orders" />
          <div className="flex gap-3 items-center">
            <FilterButton />
          </div>
        </div>
        <div className="px-8">
          <OrderMorePagesButtons activeButton="" />
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader size={60} />{" "}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={orderData}
            handleChange={handleChange}
            handleRow={handleRow}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalOrders,
              showSizeChanger: true,
            }}
          />
        )}
      </div>
    </>
  );
};

export default Orders;
